import axios from 'axios'
import store from '@/store'
import { captureLog } from '@/helpers/log'

const apiVersionRegex = /\/v[0-9]\//

export function getCancelToken() {
	var CancelToken = axios.CancelToken
	var source = CancelToken.source()
	return source
}

export function isCanceled(thrown) {
	return axios.isCancel(thrown)
}

export function get(url, cancelToken, options = {}) {
	return request({
		method: 'GET',
		url,
		data: null,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

export function post(url, data, cancelToken, options = {}) {
	return request({
		method: 'POST',
		url,
		data,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

export function put(url, data, cancelToken, options = {}) {
	return request({
		method: 'PUT',
		url,
		data,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

export function patch(url, data, cancelToken, options = {}) {
	return request({
		method: 'PATCH',
		url,
		data,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

// delete is reserved keyword
export function del(url, data, cancelToken, options = {}) {
	return request({
		method: 'DELETE',
		url,
		data,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

export function externalRequest(type, url, data, cancelToken, options = {}) {
	return customRequest({
		method: type,
		url,
		data,
		cancelToken: cancelToken ? cancelToken.token : null,
		...options,
	})
}

const log = (phase, options = {}, ...extras) => {
	const { method, url, cancelToken, data } = options
	captureLog('API', phase, `${method} ${url}`, data, ...extras)
}

export const request = async (options = {}) => {
	const { PROD, VITE_API_URL: apiUrl } = import.meta.env
	const { auth, timezone, config } = store
	const deviceType = config.device_type || 'WEB'
	const version = `${deviceType}/${config.buildNumber || 1}/0`

	// Route api call to proper environment
	if (PROD && apiUrl && options.url?.startsWith('/api')) {
		const apiHost = apiVersionRegex.test(options.url) ? apiUrl : `${apiUrl}/v1/`
		options.url = options.url.replace('/api', apiHost)
	}

	try {
		log('init', options)
		const response = await axios({
			...options,
			headers: {
				Authorization: `Bearer ${auth.api_token}`,
				'X-Time-Zone': timezone,
				'X-Version': version,
				...(options.headers || {}),
			},
		})
		log('success', options, response)
		store.setApiTokenCookie(auth.api_token)
		return response
	} catch (e) {
		log('error', options, e)
		if (e.request?.status === 401) {
			EventBus.$emit('logoutAndRedirect')
		} else {
			throw e
		}
	}
}

export const customRequest = async (options = {}) => {
	try {
		log('init', options)
		const response = await axios({
			...options,
			headers: {
				...(options.headers || {}),
			},
		})
		log('success', options, response)
		return response
	} catch (e) {
		log('error', options, e)
		throw e
	}
}

export const externalPut = async (options = {}) => {
	try {
		log('init', options)
		const response = await axios({
			...options,
			headers: {
				'X-Time-Zone': store.timezone,
				...(options.headers || {}),
			},
		})
		log('success', options, response)
		return response
	} catch (e) {
		log('error', options, e)
		throw e
	}
}

export function interceptors(cb) {
	axios.interceptors.response.use(
		(res) => {
			return res
		},
		(err) => {
			cb(err)
			return Promise.reject(err)
		}
	)
}
